import { useRef } from "react"
import parse from "html-react-parser"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useNavigation } from "contexts/Navigation"
import { useEnv } from "contexts/Env"
import { useDictionary } from "contexts/Dictionary"

import { body100, bold, headline30, legend100, surtitle45 } from "css/text"
import { contrastMode } from "css/a11y"
import { buttonCommon, buttonContrastMode, buttonIcon, buttonReset, translucentDark } from "css/buttons"

import Image from "components/media/ImageCLD"
import VideoCLD, { cssContainer, cssVideo } from "components/media/VideoCLD"
import { Icon } from "components/icon/Icon"
import VideoPlayerCLD from "components/media/VideoPlayerCLD"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"

const A = styled.a`
  display: grid;
  position: relative;
  width: 100%;
  text-decoration: none;
  overflow: hidden;

  & * {
    pointer-events: none;
  }

  color: rgb(var(--light-black));

  &.dark-theme {
    color: rgb(var(--pure-white));
  }

  &:focus img {
    transform: scale(1.05) translate3d(0, 0, 0);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover img {
      transform: scale(1.05) translate3d(0, 0, 0);
    }
  }
`

const ButtonPlayer = styled.button`
  ${buttonReset}
  display: grid;
  position: relative;
  width: 100%;
  text-align: start;

  color: rgb(var(--light-black));

  &.dark-theme {
    color: rgb(var(--pure-white));
  }
`

const Figure = styled.figure`
  width: 100%;

  display: grid;
  & picture {
    display: block;
    grid-column: 1 / -1;
    grid-row: 1;
    overflow: hidden;
  }

  & .${cssContainer} {
    grid-column: 1 / -1;
    grid-row: 1;

    display: block;
    width: 100%;
    height: 100%;
  }

  & .${cssVideo} {
    height: 100%;
  }

  & img {
    grid-column: 1 / -1;
    grid-row: 1;

    display: block;
    width: 100%;
    height: auto;
    transform: scale(1) translate3d(0, 0, 0);
    transition: transform 0.4s ease-out;
  }

  &.overlay::after {
    content: "";
    display: block;
    position: absolute;
    inset-inline: 0;
    inset-block-end: 0;
    height: 50%;
    background: linear-gradient(rgba(0 0 0 / 0), rgba(0 0 0 / 0.3));
  }
`

const Container = styled.figcaption`
  display: grid;
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  grid-template-columns: 30px auto 25%;
  grid-template-rows: 30px auto min-content 30px;
`

const Hgroup = styled.hgroup`
  grid-column: 2;
  grid-row: 2;
  z-index: 1;
  align-self: end;
  position: relative;
  display: grid;
  grid-template-rows: repeat(2, min-content);
  margin-block-end: 10px;

  height: fit-content;

  & > h3 {
    grid-row-start: 2;
    grid-column: 1 / -1;

    margin: 0;
    width: 100%;
  }

  & > p {
    grid-row-start: 1;
  }
  ${contrastMode}
`

const Kicker = styled.p`
  ${surtitle45}
`

const DuraSpan = styled.span`
  ${legend100}
  ${bold}
  position: relative;
  grid-column: 2;
  grid-row: 3;
  z-index: 1;
  align-self: end;
  justify-self: start;
  line-height: 1;
  ${contrastMode}
  display: flex;
  & svg {
    margin-inline-end: 8px;
    width: 14px;
    height: 14px;
  }
`

const CTA = styled.span`
  ${legend100}
  ${bold}
  position: relative;
  grid-column: 2;
  grid-row: 3;
  z-index: 1;
  align-self: end;
  justify-self: start;
  line-height: 1;
  ${contrastMode}
  & svg {
    margin-inline: 0.5rem;
    margin-bottom: -1px;
    width: 14px;
    height: 14px;
  }
`

const Title = styled.h3`
  ${body100}
  ${bold}
  position: relative;
  grid-column: 2;
  grid-row: 2;
  z-index: 1;
  margin-block-end: 10px;
  align-self: end;
  &.onlytitle {
    ${contrastMode}
  }
`

function Media({ media }) {
  const console = useConsole()

  return media.image ? <Image {...media.image} sizes='(min-width: 48rem) 35vw, 70vw' /> : media.video ? <VideoCLD {...media.video} noButton={true} /> : null
}

function LinkAction({ href, external, theme, outlink_name, children, index, cardName, ...props }) {
  const console = useConsole()
  const navigation = useNavigation()
  const env = useEnv()
  const linkurl = external
    ? new URL(external.replaceAll(env.orgGlobalDomain, env.org), `https://${env.cloudinary.secureDistribution}`)
    : navigation.localize(href ?? "#")

  function track() {
    global.sessionStorage.setItem(
      "__ddlAddOn",
      JSON.stringify([
        "rollerRefClicked",
        {
          rollerRefInfo: {
            cardName: cardName || "",
            cardPosition: index + 1,
          },
        },
      ])
    )
  }

  function navigate(e) {
    e.preventDefault()
    track()
    navigation.navigate(e.target.getAttribute("href"))
  }

  function triggerEvent() {
    waTriggerEvent({
      eventName: "outlink",
      eventType: "Click",
      outlinkInfo: {
        linkURL: linkurl.href,
        linkName: outlink_name,
      },
    })
  }

  return (
    <A className={theme ?? ""} href={linkurl} onClick={external ? triggerEvent : navigate} target={external ? "_blank" : null} data-index={index}>
      {children}
    </A>
  )
}

function PlayerVidAction({ theme, children, index, ...props }) {
  const console = useConsole()

  const rvideo = useRef()
  const ref = useRef()

  function onClick() {
    rvideo?.current?.play()
  }
  function onClose() {
    ref.current.focus()
  }

  return (
    <>
      <ButtonPlayer className={theme} ref={ref} onClick={onClick} data-index={index}>
        {children}
      </ButtonPlayer>
      <VideoPlayerCLD ref={rvideo} {...props} onClose={onClose} />
    </>
  )
}

const actionComp = {
  link: LinkAction,
  video_player: PlayerVidAction,
}

function formDur(secs) {
  const min = Math.floor(secs / 60)
  const sec = secs % 60
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
}

export default function ActionCard({ heading, image_cld: image, theme, caption, link, action, media, index, overlay, ...props }) {
  const console = useConsole()
  const dictionary = useDictionary()

  const actionType = Object.keys(action?.[0])?.[0]
  const Action = actionComp?.[actionType] || null

  const dur = actionType === "video_player" ? action[0][actionType]?.sources[0].duration : 0
  const duration = dur ? formDur(Math.floor(dur)) : ""
  const [minutes, seconds] = duration.split(":")

  console.verbose("\n•••••>>> overlay", overlay)

  return Action ? (
    <Action {...action[0][actionType]} theme={theme} index={index} cardName={heading?.title}>
      <Figure className={overlay ? "overlay" : ""}>
        <Media media={media[0]} />
        <Container>
          {heading?.title && heading?.kicker ? (
            <Hgroup>
              {heading?.kicker && <Kicker>{heading.kicker}</Kicker>}
              {heading?.title && <Title>{heading.title}</Title>}
            </Hgroup>
          ) : heading?.title ? (
            <Title className='onlytitle'>{heading.title}</Title>
          ) : null}
          {actionType === "video_player" && duration ? (
            <span aria-label={dictionary.duration(minutes, seconds)} style={{ display: "contents" }}>
              <DuraSpan aria-hidden={true}>
                <Icon type='play' />
                {dictionary.durationShort(minutes, seconds)}
              </DuraSpan>
            </span>
          ) : null}
          {actionType === "link" ? (
            <CTA>
              {dictionary.discoverMore()}
              {/* <Icon type='chevron' /> */}
            </CTA>
          ) : null}
        </Container>
      </Figure>
    </Action>
  ) : null
}
